import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Switch,
  FormControlLabel,
  Snackbar,
  Alert,
  CircularProgress,
  InputAdornment,
  Tooltip,
  IconButton,
  Chip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Plus,
  X,
  Save,
  Search,
  Hotel,
  Building,
  Settings,
  Eye,
  EyeOff,
  CheckCircle2,
  XCircle,
  Copy, Link, Unlink, MapPin
} from 'lucide-react';
import api from '../services/api';
import { useRefresh } from '../contexts/RefreshContext';

const HotelsPage = () => {
  const [hotels, setHotels] = useState([]);
  const [filteredHotels, setFilteredHotels] = useState([]);
  const [loading, setLoading] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [newHotelName, setNewHotelName] = useState('');

  // Square config dialog
  const [squareDialogOpen, setSquareDialogOpen] = useState(false);
  const [squareSaving, setSquareSaving] = useState(false);
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [addressOpen, setAddressOpen] = useState(false);
  const [savingAddress, setSavingAddress] = useState(false);
  const [addressForm, setAddressForm] = useState({ id: null, name: '', address: '' });

  const [squareForm, setSquareForm] = useState({
    square_sync_enabled: false,
    square_location_id: '',
    square_webhook_url: '',
    square_webhook_signature_key: '',
  });
  const [showKey, setShowKey] = useState(false);

  const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    showSnackbar('Copied to clipboard!');
  } catch (e) {
    showSnackbar('Could not copy', 'error');
  }
};


  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [search, setSearch] = useState('');
  const { refreshKey } = useRefresh();

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchHotels = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/hotels');
      setHotels(response.data || []);
      setFilteredHotels(response.data || []);
    } catch (error) {
      console.error('Error fetching hotels:', error);
      showSnackbar('Error loading hotels', 'error');
      setHotels([]);
      setFilteredHotels([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHotels();
  }, [refreshKey]);

  useEffect(() => {
    fetchHotels();
  }, [fetchHotels]);

  useEffect(() => {
    const query = search.toLowerCase().trim();
    if (!query) {
      setFilteredHotels(hotels);
      return;
    }
    const filtered = hotels.filter((h) => h.name.toLowerCase().includes(query));
    setFilteredHotels(filtered);
  }, [search, hotels]);

  const handleAddHotel = () => {
    setNewHotelName('');
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  /**
   * iOS-like switch (clean + obvious on/off)
   */
  const IOSSwitch = styled((props) => (
    <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
  ))(({ theme }) => ({
    width: 46,
    height: 28,
    padding: 0,
    '& .MuiSwitch-switchBase': {
      padding: 2,
      transitionDuration: '200ms',
      '&.Mui-checked': {
        transform: 'translateX(18px)',
        color: '#fff',
        '& + .MuiSwitch-track': {
          backgroundColor: theme.palette.mode === 'dark' ? '#2ECA45' : '#34C759', // iOS green
          opacity: 1,
        },
        '& .MuiSwitch-thumb': {
          boxShadow: '0 3px 10px rgba(0,0,0,0.18)',
        },
      },
      '&.Mui-focusVisible .MuiSwitch-thumb': {
        border: '6px solid #fff',
      },
    },
    '& .MuiSwitch-thumb': {
      boxSizing: 'border-box',
      width: 24,
      height: 24,
      borderRadius: 999,
      backgroundColor: '#fff',
      boxShadow: '0 3px 10px rgba(0,0,0,0.16)',
    },
    '& .MuiSwitch-track': {
      borderRadius: 999,
      backgroundColor: theme.palette.mode === 'dark' ? '#39393D' : '#E5E5EA', // iOS off gray
      opacity: 1,
      transition: theme.transitions.create(['background-color'], { duration: 200 }),
    },
  }));

  const handleSaveNewHotel = async () => {
    const trimmedName = newHotelName.trim();
    if (!trimmedName) {
      showSnackbar('Hotel name is required', 'warning');
      return;
    }

    setLoading(true);
    try {
      await api.post('/admin/hotels', { name: trimmedName });
      showSnackbar(`Hotel "${trimmedName}" added successfully`);
      handleDialogClose();
      fetchHotels();
    } catch (error) {
      console.error('Error adding hotel:', error);
      const errorMsg = error.response?.data?.msg || 'Error adding hotel';
      showSnackbar(errorMsg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusToggle = async (hotelId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';

    // optimistic
    setHotels((prev) => prev.map((h) => (h.id === hotelId ? { ...h, status: newStatus } : h)));

    try {
      await api.put(`/admin/hotels/${hotelId}/status`, { status: newStatus });
      showSnackbar(`Hotel status updated to ${newStatus}`);
    } catch (error) {
      console.error('Error updating hotel status:', error);
      showSnackbar('Error updating status', 'error');
      // rollback
      setHotels((prev) => prev.map((h) => (h.id === hotelId ? { ...h, status: currentStatus } : h)));
    }
  };

const handleToggleSandart = async (hotelId, current) => {
  const next = !current;
  setHotels((prev) => prev.map((h) => (h.id === hotelId ? { ...h, is_sandart: next } : h)));
  try {
    await api.put(`/admin/hotels/${hotelId}/sandart`, { is_sandart: next });
    showSnackbar(next ? 'Sand Art checklists enabled' : 'Back to the standard checklists');
  } catch (error) {
    console.error('Error updating sandart:', error);
    showSnackbar('Error updating Sand Art', 'error');
    setHotels((prev) => prev.map((h) => (h.id === hotelId ? { ...h, is_sandart: current } : h)));
  }
};

const openAddressDialog = (hotel) => {
  setAddressForm({ id: hotel.id, name: hotel.name, address: hotel.address || '' });
  setAddressOpen(true);
};

const handleSaveAddress = async () => {
  const { id, address } = addressForm;
  setSavingAddress(true);
  try {
    await api.put(`/admin/hotels/${id}/address`, { address });
    setHotels((prev) => prev.map((h) => (h.id === id ? { ...h, address: address.trim() || null } : h)));
    setAddressOpen(false);
    showSnackbar('Address saved');
  } catch (error) {
    console.error('Error saving address:', error);
    showSnackbar('Error saving address', 'error');
  } finally {
    setSavingAddress(false);
  }
};

const openSquareConfig = (hotel) => {
  const base = 'https://miniboss-backend.onrender.com';
  const autoUrl = `${base}/square/webhook/hotel/${hotel.id}`;

  setSelectedHotel(hotel);
  setSquareForm({
    square_sync_enabled: !!hotel.square_sync_enabled,
    square_location_id: hotel.square_location_id || '',
    // se vier vazio do DB, já mostra a automática
    square_webhook_url: (hotel.square_webhook_url || '').trim() || autoUrl,
    square_webhook_signature_key: hotel.square_webhook_signature_key || '',
  });
  setShowKey(false);
  setSquareDialogOpen(true);
};


  const closeSquareConfig = () => {
    setSquareDialogOpen(false);
    setSelectedHotel(null);
  };

  const handleSaveSquareConfig = async () => {
  if (!selectedHotel) return;

  const payload = {
    square_sync_enabled: !!squareForm.square_sync_enabled,
    square_location_id: (squareForm.square_location_id || '').trim(),
    square_webhook_signature_key: (squareForm.square_webhook_signature_key || '').trim(),
  };

  try {
    const res = await api.put(
      `/admin/hotels/${selectedHotel.id}/square`,
      payload
    );

    const updatedHotel = res.data?.hotel;

    if (updatedHotel) {
      setHotels((prev) =>
        prev.map((h) =>
          h.id === updatedHotel.id ? updatedHotel : h
        )
      );
    }

    showSnackbar('Square config saved!');
    setSquareDialogOpen(false);
  } catch (err) {
    console.error(err);
    showSnackbar('Error saving Square config', 'error');
  }
};


  const StatusPill = ({ status }) => {
    const isActive = status === 'active';
    return (
      <Chip
        icon={isActive ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
        label={isActive ? 'ACTIVE' : 'INACTIVE'}
        size="small"
        sx={{
          fontWeight: 900,
          letterSpacing: '0.06em',
          borderRadius: 999,
          px: 0.5,
          color: isActive ? '#0F5132' : '#842029',
          bgcolor: isActive ? 'rgba(25, 135, 84, 0.14)' : 'rgba(220, 53, 69, 0.14)',
          border: isActive ? '1px solid rgba(25, 135, 84, 0.30)' : '1px solid rgba(220, 53, 69, 0.30)',
          '& .MuiChip-icon': {
            color: isActive ? '#198754' : '#DC3545',
          },
        }}
      />
    );
  };

  const SquarePill = ({ enabled }) => {
    const on = !!enabled;
    return (
      <Chip
        icon={on ? <Link size={16} /> : <Unlink size={16} />}
        label={on ? 'LINKED' : 'UNLINKED'}
        size="small"
        sx={{
          fontWeight: 900,
          letterSpacing: '0.06em',
          borderRadius: 999,
          px: 0.5,
          color: on ? '#0B3D2E' : '#3D3D3D',
          bgcolor: on ? 'rgba(52, 199, 89, 0.16)' : 'rgba(120, 120, 128, 0.14)',
          border: on ? '1px solid rgba(52, 199, 89, 0.35)' : '1px solid rgba(120, 120, 128, 0.28)',
          '& .MuiChip-icon': {
            color: on ? '#34C759' : '#8E8E93',
          },
        }}
      />
    );
  };

  return (
    <Box p={1}>
      <Box
        sx={{
          pb: 1,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: 'center',
          gap: 1,
          maxWidth: 1000,
          margin: '0 auto',
        }}
      >
        <TextField
          size="small"
          placeholder="Search hotel..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          variant="outlined"
          sx={{
            flex: 7,
            backgroundColor: '#fff',
            borderRadius: 5,
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
            width: { xs: '100%', sm: 'auto' },
            '& .MuiOutlinedInput-root': { '& fieldset': { border: 'none' } },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={18} />
              </InputAdornment>
            ),
          }}
        />

        <Button
          variant="contained"
          onClick={handleAddHotel}
          startIcon={<Plus size={18} />}
          sx={{
            flex: 3,
            height: '40px',
            whiteSpace: 'nowrap',
            width: { xs: '100%', sm: 'auto' },
            background: 'linear-gradient(135deg, #1976d2, #0d47a1)',
            color: '#fff',
            fontWeight: 900,
            borderRadius: 999,
            boxShadow: '0 10px 24px rgba(13, 71, 161, 0.18)',
            '&:hover': { background: 'linear-gradient(135deg, #1565c0, #003c8f)' },
          }}
        >
          New Hotel
        </Button>
      </Box>

      <Box
        sx={{
          overflowX: 'auto',
          overflowY: 'auto',
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
          maxWidth: 1000,
          margin: '0 auto',
          width: '100%',
          maxHeight: 'calc(100vh - 150px)',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        <Paper sx={{ backgroundColor: '#fff' }}>
          <TableContainer>
            <Table stickyHeader size="small" sx={{ backgroundColor: '#fff' }}>
              <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 900, width: '10%' }}>ID</TableCell>
                  <TableCell sx={{ fontWeight: 900 }}>Name</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 900, width: '14%' }}>
                    Status
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 900, width: '16%' }}>
                    Checklists
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 900, width: '22%' }}>
                    Square
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading && filteredHotels.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                      <CircularProgress size={30} />
                    </TableCell>
                  </TableRow>
                ) : filteredHotels.length === 0 && !loading ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                      <Typography color="text.secondary">No hotel found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredHotels.map((hotel) => (
                    <TableRow
                      key={hotel.id}
                      hover
                      sx={{
                        '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.03)' },
                        '& td': { py: 1.1 },
                      }}
                    >
                      <TableCell sx={{ fontWeight: 800 }}>{hotel.id}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        {hotel.name}
                        {/* Endereço fica junto do nome: é o que a funcionária do
                            material usa para abrir o hotel no maps. */}
                        <Box
                          onClick={() => openAddressDialog(hotel)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openAddressDialog(hotel); }
                          }}
                          sx={{
                            mt: 0.4, display: 'flex', alignItems: 'center', gap: 0.5,
                            cursor: 'pointer', color: hotel.address ? 'text.secondary' : '#b0812a',
                            fontSize: 12.5, fontWeight: 600, maxWidth: 320,
                            '&:hover': { textDecoration: 'underline' },
                            '&:focus-visible': { outline: '2px solid #1976d2', outlineOffset: 2 },
                          }}
                        >
                          <MapPin size={13} style={{ flexShrink: 0 }} />
                          <Box component="span" sx={{
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          }}>
                            {hotel.address || 'Add address'}
                          </Box>
                        </Box>
                      </TableCell>

                      <TableCell align="center">
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.6 }}>

                          <Tooltip title={hotel.status === 'active' ? 'Deactivate' : 'Activate'}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <IOSSwitch
                                checked={hotel.status === 'active'}
                                onChange={() => handleStatusToggle(hotel.id, hotel.status)}
                              />
                            </Box>
                          </Tooltip>
                        </Box>
                      </TableCell>

                      <TableCell align="center">
                        {/* Sand Art: troca os checklists de opening e closing
                            que o POS mostra para este resort. */}
                        <Tooltip title={hotel.is_sandart
                          ? 'Sand Art checklists (opening/closing)'
                          : 'Uses the standard checklists'}>
                          <Chip
                            label={hotel.is_sandart ? '🏖️ SAND ART' : 'STANDARD'}
                            size="small"
                            onClick={() => handleToggleSandart(hotel.id, hotel.is_sandart)}
                            sx={{
                              fontWeight: 900, cursor: 'pointer', borderRadius: 999,
                              bgcolor: hotel.is_sandart ? 'rgba(240,162,2,.18)' : '#f0f0f0',
                              color: hotel.is_sandart ? '#8a5c00' : '#777',
                              border: `1.5px solid ${hotel.is_sandart ? 'rgba(240,162,2,.6)' : '#ddd'}`,
                            }}
                          />
                        </Tooltip>
                      </TableCell>

                      <TableCell align="center">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                          <SquarePill enabled={hotel.square_sync_enabled} />

                          <Tooltip title="Square config">
                            <Button
                              onClick={() => openSquareConfig(hotel)}
                              startIcon={<Settings size={18} />}
                              sx={{
                                borderRadius: 999,
                                fontWeight: 900,
                                textTransform: 'none',
                                px: 1.6,
                                py: 0.6,
                                color: '#111',
                                border: '1px solid rgba(0,0,0,0.12)',
                                bgcolor: 'rgba(255,255,255,0.9)',
                                '&:hover': { bgcolor: 'rgba(245,245,245,1)' },
                              }}
                            >
                              Config
                            </Button>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      {/* New Hotel Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 900,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Hotel size={24} color="#1976d2" />
            <span>New Hotel</span>
          </Box>
          {loading && <CircularProgress size={24} />}
        </DialogTitle>

        <DialogContent dividers>
          <Typography variant="subtitle1" fontWeight={900} mb={2} sx={{ color: '#333' }}>
            Hotel Information
          </Typography>

          <TextField
            required
            fullWidth
            id="name"
            label="Hotel Name"
            name="name"
            value={newHotelName}
            onChange={(e) => setNewHotelName(e.target.value)}
            autoFocus
            placeholder="Enter hotel name..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Building size={18} color="#666" />
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>

        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Button
            onClick={handleDialogClose}
            startIcon={<X size={18} />}
            fullWidth
            sx={{
              fontWeight: 900,
              backgroundColor: '#f5f5f5',
              color: '#333',
              border: '1px solid #ddd',
              borderRadius: 999,
              '&:hover': { backgroundColor: '#eaeaea' },
            }}
          >
            Cancel
          </Button>

          <Button
            onClick={handleSaveNewHotel}
            variant="contained"
            startIcon={<Save size={18} />}
            disabled={loading}
            fullWidth
            sx={{
              fontWeight: 900,
              background: 'linear-gradient(135deg, #1976d2, #0d47a1)',
              color: '#fff',
              borderRadius: 999,
              boxShadow: '0 10px 24px rgba(13, 71, 161, 0.18)',
              '&:hover': { background: 'linear-gradient(135deg, #1565c0, #003c8f)' },
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Create Hotel'}
          </Button>
        </Box>
      </Dialog>

      {/* Square Config Dialog */}
      <Dialog
        open={squareDialogOpen}
        onClose={closeSquareConfig}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 900,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Settings size={22} />
            <span>Square Config {selectedHotel ? `— ${selectedHotel.name}` : ''}</span>
          </Box>
          {squareSaving && <CircularProgress size={22} />}
        </DialogTitle>

        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Big clear enable block */}
            <Box
              sx={{
                p: 1.6,
                borderRadius: 2,
                border: squareForm.square_sync_enabled
                  ? '1px solid rgba(52, 199, 89, 0.35)'
                  : '1px solid rgba(120, 120, 128, 0.28)',
                bgcolor: squareForm.square_sync_enabled ? 'rgba(52, 199, 89, 0.10)' : 'rgba(120, 120, 128, 0.08)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 1000, letterSpacing: '0.06em' }}>
                  {squareForm.square_sync_enabled ? 'LINK ENABLED' : 'LINK DISABLED'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  When enabled, POS will pull tips from Square and allow linking payments.
                </Typography>
              </Box>

              <IOSSwitch
                checked={!!squareForm.square_sync_enabled}
                onChange={(e) =>
                  setSquareForm((prev) => ({ ...prev, square_sync_enabled: e.target.checked }))
                }
              />
            </Box>

            <TextField
              label="Square Location ID"
              value={squareForm.square_location_id}
              onChange={(e) => setSquareForm((prev) => ({ ...prev, square_location_id: e.target.value }))}
              placeholder="Ex: S8GWD5R9QB376"
              fullWidth
            />

           <TextField
  label="Square Webhook URL"
  value={squareForm.square_webhook_url}
  fullWidth
  inputProps={{ readOnly: true }}
  onClick={() => squareForm.square_webhook_url && copyToClipboard(squareForm.square_webhook_url)}
  sx={{ cursor: 'pointer' }}
  InputProps={{
    endAdornment: (
      <InputAdornment position="end">
        <Tooltip title="Copy">
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              if (squareForm.square_webhook_url) copyToClipboard(squareForm.square_webhook_url);
            }}
            edge="end"
          >
            {/* pode usar qualquer ícone; exemplo com lucide */}
            <Copy size={18} />
          </IconButton>
        </Tooltip>
      </InputAdornment>
    ),
  }}
/>


            <TextField
              label="Square Webhook Signature Key"
              type={showKey ? 'text' : 'password'}
              value={squareForm.square_webhook_signature_key}
              onChange={(e) =>
                setSquareForm((prev) => ({ ...prev, square_webhook_signature_key: e.target.value }))
              }
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowKey((v) => !v)} edge="end">
                      {showKey ? <EyeOff size={18} /> : <Eye size={18} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </DialogContent>

        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Button
            onClick={closeSquareConfig}
            startIcon={<X size={18} />}
            fullWidth
            sx={{
              fontWeight: 900,
              backgroundColor: '#f5f5f5',
              color: '#333',
              border: '1px solid #ddd',
              borderRadius: 999,
              '&:hover': { backgroundColor: '#eaeaea' },
            }}
          >
            Cancel
          </Button>

          <Button
            onClick={handleSaveSquareConfig}
            variant="contained"
            startIcon={<Save size={18} />}
            disabled={squareSaving}
            fullWidth
            sx={{
              fontWeight: 900,
              background: squareForm.square_sync_enabled
                ? 'linear-gradient(135deg, #34C759, #0F9D58)'
                : 'linear-gradient(135deg, #6C757D, #495057)',
              color: '#fff',
              borderRadius: 999,
              boxShadow: squareForm.square_sync_enabled
                ? '0 10px 24px rgba(52, 199, 89, 0.18)'
                : '0 10px 24px rgba(108, 117, 125, 0.16)',
              '&:hover': {
                background: squareForm.square_sync_enabled
                  ? 'linear-gradient(135deg, #2FB84F, #0C8C4E)'
                  : 'linear-gradient(135deg, #5C636A, #3F454B)',
              },
            }}
          >
            {squareSaving ? <CircularProgress size={24} color="inherit" /> : 'Save Square Config'}
          </Button>
        </Box>
      </Dialog>

      {/* ── Endereço do hotel ── */}
      <Dialog
        open={addressOpen}
        onClose={() => setAddressOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3, p: 2.5 } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <MapPin size={20} />
          <DialogTitle sx={{ p: 0, fontWeight: 900, fontSize: 18 }}>
            {addressForm.name}
          </DialogTitle>
        </Box>

        <TextField
          fullWidth
          multiline
          minRows={2}
          label="Address"
          placeholder="1234 Resort Blvd, Orlando, FL 32821"
          value={addressForm.address}
          onChange={(e) => setAddressForm((p) => ({ ...p, address: e.target.value }))}
          autoFocus
        />
        <Box sx={{ mt: 1, mb: 2, fontSize: 12.5, color: 'text.secondary' }}>
          Used by the Material page to open this resort in the phone&apos;s maps app.
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            onClick={() => setAddressOpen(false)}
            fullWidth
            sx={{
              fontWeight: 900, backgroundColor: '#f2f2f2', color: '#333',
              border: '1px solid #ddd', borderRadius: 999,
              '&:hover': { backgroundColor: '#eaeaea' },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveAddress}
            variant="contained"
            startIcon={<Save size={18} />}
            disabled={savingAddress}
            fullWidth
            sx={{
              fontWeight: 900, borderRadius: 999,
              background: 'linear-gradient(135deg, #1976d2, #0d47a1)',
            }}
          >
            {savingAddress ? <CircularProgress size={22} color="inherit" /> : 'Save address'}
          </Button>
        </Box>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default HotelsPage;
